import React from 'react';
import PropTypes from 'prop-types';
import TagChip from '../../atoms/TagChip/TagChip';
import { useViewSettings } from '../../../context/ViewSettingsContext';

/**
 * ArticleTags - Componente molecular que gerencia a lista de tags do artigo
 * 
 * Este componente segue o princípio do Atomic Design como uma "molécula".
 */
const ArticleTags = ({
  tags = [],
  variant = 'default',
  onTagClick,
  onTagRemove,
  className = '',
  ...props
}) => {
  const { fontSize } = useViewSettings();
  // Calcular fontSize 20% menor para todas as variantes
  let customFontSize = undefined;
  if (fontSize) {
    const match = fontSize.match(/([\d.]+)(rem|px|em)?/);
    if (match) {
      const value = parseFloat(match[1]);
      const unit = match[2] || 'rem';
      // Diminuir 35% do tamanho base para ficar menor que os títulos
      customFontSize = (value * 0.65) + unit;
    }
  }
  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: variant === 'compact' ? '6px' : '12px',
      ...(variant === 'compact' && { marginBottom: '4px', minHeight: 20, height: 20 }),
      ...(variant === 'minimal' && { display: 'none' }),
      minHeight: variant === 'compact' ? 20 : 26,
      height: variant === 'compact' ? 20 : 26
    },
    tagsList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: variant === 'compact' ? '6px' : '12px',
      flexGrow: 1,
      minHeight: variant === 'compact' ? 20 : 26,
      height: variant === 'compact' ? 20 : 26
    }
  };

  // Mapeamento de variantes para os tags
  const tagVariantMap = {
    default: 'default',
    compact: 'small',
    minimal: 'minimal',
    detailed: 'default',
    list: 'small',
    chiclet: 'default'
  };

  // Se for minimal, não renderiza nada
  if (variant === 'minimal') return null;

  const tagVariant = tagVariantMap[variant] || 'default';
  const hasTags = tags.length > 0;

  // DEBUG: Verificar se onTagRemove está chegando
  console.log('ArticleTags', { tags, onTagRemove });

  return (
    <div 
      className={`article-tags article-tags--${variant} ${className}`}
      style={styles.container}
      {...props}
    >
      <div
        className="article-tags__list"
        style={styles.tagsList}
      >
        {hasTags && tags.map(tag => (
          <TagChip
            key={tag._id + '-' + tag.name}
            label={tag.name}
            onClick={onTagClick ? () => onTagClick(tag) : undefined}
            onDelete={onTagRemove ? () => { console.log('onTagRemove chamado com:', tag); onTagRemove(tag); } : undefined}
            size={tagVariant === 'small' ? 'small' : 'medium'}
            variant={tagVariant === 'outlined' ? 'outlined' : 'filled'}
            fontSize={customFontSize}
          />
        ))}
      </div>
    </div>
  );
};

ArticleTags.propTypes = {
  tags: PropTypes.array.isRequired,
  variant: PropTypes.string,
  onTagClick: PropTypes.func,
  onTagRemove: PropTypes.func,
  className: PropTypes.string
};

export default ArticleTags;